import * as Dialog from '@radix-ui/react-dialog';
import { X, Phone, LogIn, BookOpen, Home, Layers, LifeBuoy, Info, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { reportPhoneConversion } from '@/utils/analytics';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/home';
    return location.pathname.startsWith(path);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
  <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in-0 duration-200" />
  <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-full max-w-xs bg-gradient-to-b from-white to-gray-50 p-6 shadow-2xl animate-in slide-in-from-left-4 duration-300">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-xl font-semibold text-gray-900">
              Menu
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-gray-600 hover:text-gray-900"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <nav className="mt-8 space-y-2" aria-label="Main navigation">
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/')}
              aria-current={isActive('/') ? 'page' : undefined}
            >
              <Home className="h-4 w-4 mr-2" aria-hidden="true" /> Home
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/services')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/services')}
              aria-current={isActive('/services') ? 'page' : undefined}
            >
              <Layers className="h-4 w-4 mr-2" aria-hidden="true" /> Services
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/about')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/about')}
              aria-current={isActive('/about') ? 'page' : undefined}
            >
              <Info className="h-4 w-4 mr-2" aria-hidden="true" /> About Us
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/support')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/support')}
              aria-current={isActive('/support') ? 'page' : undefined}
            >
              <LifeBuoy className="h-4 w-4 mr-2" aria-hidden="true" /> Support
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/blog')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/blog')}
              aria-current={isActive('/blog') ? 'page' : undefined}
            >
              <BookOpen className="h-4 w-4 mr-2" aria-hidden="true" /> Blog
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/resources')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/resources')}
              aria-current={isActive('/resources') ? 'page' : undefined}
            >
              <BookOpen className="h-4 w-4 mr-2" aria-hidden="true" /> Resources
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start transition-all duration-200",
                isActive('/contact')
                  ? "bg-[#e69138]/10 text-[#990000] font-semibold border-l-4 border-[#e69138]"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
              )}
              onClick={() => handleNavigation('/contact')}
              aria-current={isActive('/contact') ? 'page' : undefined}
            >
              <Mail className="h-4 w-4 mr-2" aria-hidden="true" /> Contact
            </Button>

            <div className="pt-4 space-y-3">
              {config.fieldVeroUrl !== '#' ? (
                <a
                  href={config.fieldVeroUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e69138]"
                  aria-label="Access FieldVero Portal"
                >
                  <LogIn className="h-4 w-4" /> Portal
                </a>
              ) : (
                <span
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-400 cursor-not-allowed"
                  aria-label="Portal (Coming Soon)"
                >
                  <LogIn className="h-4 w-4" /> Portal
                </span>
              )}
              <a
                href="tel:8005402140"
                onClick={(event) => {
                  event.preventDefault();
                  reportPhoneConversion();
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#e69138] to-[#d17f2a] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e69138]"
                aria-label="Call (800) 540-2140"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
            </div>
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
